import type { GameSummary } from '@cardengine/shared';
import { linkProps } from '../router.js';

interface Pile {
  name: string;
  body: string;
}

const PILES: Pile[] = [
  {
    name: 'The tableau',
    body: 'Seven columns, holding one card through to seven. Only the bottom card of each column starts face up; the rest are turned over as you clear the cards covering them.',
  },
  {
    name: 'The foundations',
    body: 'Four empty spaces, one per suit. These are what you are filling: each starts with an Ace and builds up in order to the King. Fill all four and the game is won.',
  },
  {
    name: 'The stock',
    body: 'The twenty-four cards left over after the deal, face down. Turning them over is how you reach the cards the tableau has not given you.',
  },
  {
    name: 'The waste',
    body: 'Where the stock goes once turned. Only the top card is in play, so what sits underneath is a matter of how many you turn at a time.',
  },
];

export function SolitaireHowToPlay({ game }: { game: GameSummary }) {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">How to play</p>
        <h1 className="prose__title">{game.name}</h1>
        <p className="prose__lead">
          Get all fifty-two cards onto the four foundations, Ace through King, one suit each.
        </p>
      </header>

      <section className="prose__section">
        <h2>The table</h2>
        <ul className="prose__list">
          {PILES.map((pile) => (
            <li key={pile.name}>
              <div>
                <h3>{pile.name}</h3>
                <p>{pile.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="prose__section">
        <h2>Moving cards</h2>
        <p>
          The tableau builds <strong>down</strong> in <strong>alternating colours</strong>: a red nine takes
          a black eight, which takes a red seven. The foundations do the opposite — <strong>up</strong>, and{' '}
          <strong>in suit</strong>, starting with the Ace.
        </p>
        <p>
          A run of cards already in sequence moves as one, so you can lift a whole ladder onto a card that
          fits its top. Empty a column and only a <strong>King</strong> may move into the gap, which is why
          emptying one is worth less than it looks.
        </p>
        <p>
          Click a card to lift it and the board lights up wherever it can go; click it again to send it
          straight to its foundation. Clicking a lifted card with nowhere sensible to go simply puts it back.
        </p>
      </section>

      <section className="prose__section">
        <h2>The stock is the difficulty</h2>
        <p>
          Turning <strong>one card at a time</strong> means every card in the stock is reachable, and most
          deals can be solved with enough patience. Turning <strong>three</strong> means only every third
          card comes up on a given pass — the two beneath it stay buried until the order shifts.
        </p>
        <p>
          How many times you may go back through the stock matters just as much. Unlimited passes are
          forgiving; a single pass, three at a time, is the version where roughly one deal in six is
          winnable at all.
        </p>
      </section>

      <section className="prose__section">
        <h2>Knowing when it is over</h2>
        <p>
          Not every deal can be won, and there is no shame in a dead one. When nothing on the board will
          move and nothing still to come can help, the game says so rather than leaving you to cycle the
          stock hoping. Once nothing is face down, the rest is bookkeeping — take the finish and go again.
        </p>
      </section>

      <div className="prose__cta">
        <a className="btn btn--primary btn--large" {...linkProps(game.href)}>
          Play {game.name}
        </a>
      </div>
    </main>
  );
}
